/**
 * Phaser engine bootstrapper (stub).
 *
 * The real implementation will lazy-load Phaser to keep the base bundle lean.
 * For now we outline the interface so dependent components can compile and
 * we can iterate on state wiring/testing before the renderer is shipped.
 */

export type EngineStatus = 'idle' | 'initialising' | 'ready' | 'error';

type PhaserType = typeof import('phaser');

interface EngineInitOptions {
  container: HTMLElement;
  debug?: boolean;
}

interface EngineHandle {
  status: EngineStatus;
  destroy: () => Promise<void>;
}

let status: EngineStatus = 'idle';
let gameInstance: import('phaser').Game | null = null;

export const getEngineStatus = (): EngineStatus => status;

const buildGameConfig = async (Phaser: PhaserType, container: HTMLElement, debug = false) => {
  // Validate container is attached to DOM
  if (!container || !container.parentElement) {
    throw new Error('[PhaserEngine] Container must be attached to DOM');
  }

  // Validate container has dimensions
  const { clientWidth, clientHeight } = container;
  if (clientWidth === 0 || clientHeight === 0) {
    // Try getBoundingClientRect as fallback
    const rect = container.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      throw new Error('[PhaserEngine] Container must have dimensions');
    }
  }

  // Dynamically import WorldScene
  const { WorldScene } = await import('../scenes/WorldScene');

  const config: Phaser.Types.Core.GameConfig = {
    type: Phaser.AUTO,
    width: Math.max(clientWidth, 800),
    height: Math.max(clientHeight, 600),
    parent: container, // Use HTMLElement directly for reliable attachment
    backgroundColor: '#000000',
    pixelArt: true,
    antialias: false,
    scene: [WorldScene],
    physics: {
      default: 'arcade',
      arcade: {
        debug: debug,
        gravity: { x: 0, y: 0 } // Top-down, no gravity
      }
    },
    render: {
      transparent: false,
      clearBeforeRender: true,
      pixelArt: true
    }
  };

  return config;
};

export const initialisePhaserEngine = async (
  options: EngineInitOptions
): Promise<EngineHandle> => {
  const { container, debug = false } = options;

  if (!container) {
    throw new Error('[PhaserEngine] Container element is required');
  }

  status = 'initialising';

  try {
    console.log('[PhaserEngine] Starting initialization...');

    if (gameInstance) {
      console.log('[PhaserEngine] Destroying existing instance...');
      await gameInstance.destroy(true);
      gameInstance = null;
    }

    console.log('[PhaserEngine] Loading Phaser module...');
    const PhaserModule = await import('phaser');
    const Phaser: PhaserType = PhaserModule.default ?? PhaserModule;
    console.log('[PhaserEngine] Phaser module loaded');

    console.log('[PhaserEngine] Building game config...');
    const config = await buildGameConfig(Phaser, container, debug);
    console.log('[PhaserEngine] Config built:', {
      width: config.width,
      height: config.height,
      parent: config.parent,
      scenes: config.scene?.length
    });

    console.log('[PhaserEngine] Creating Phaser.Game instance...');
    gameInstance = new Phaser.Game(config);
    console.log('[PhaserEngine] Phaser.Game created successfully');
    status = 'ready';

    return {
      status,
      destroy: async () => {
        if (gameInstance) {
          console.log('[PhaserEngine] Destroying game instance...');
          await gameInstance.destroy(true);
          gameInstance = null;
        }
        status = 'idle';
      },
    };
  } catch (error) {
    console.error('[PhaserEngine] Failed to initialise Phaser:', error);
    status = 'error';
    throw error;
  }
};
